import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { ButtonCTA1, LoadingCircle } from "@givesafe/design-system-react";

import samaritanLogo from "../../assets/logo-white.png";
import useValidateToken from "../../hooks/api/useValidateToken";
import classes from "./index.module.scss";

const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 1900 + 1 }, (_, i) =>
  String(CURRENT_YEAR - i)
);

const daysInMonth = (month, year) => {
  if (!month) return 31;
  const y = year ? Number(year) : 2000; // leap-year-safe default when year not chosen yet
  return dayjs(`${y}-${month}-01`).daysInMonth();
};

/** Wire format for magic-link verify: YYYY-MM-DD */
const formatDobForApi = (month, day, year) => `${year}-${month}-${day}`;

const isUsedLinkError = (message) =>
  typeof message === "string" && /already been used/i.test(message);

/**
 * DOB confirmation for magic-link login.
 * Verifies via POST /verify_login_link { token, date_of_birth }.
 */
const MagicLinkDobGate = ({ token, linkSetupError, onVerified, onGoToLogin }) => {
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [year, setYear] = useState("");
  const [submitError, setSubmitError] = useState("");
  const { validateToken, isLoading } = useValidateToken(setSubmitError);
  const errorMessage = linkSetupError || submitError;
  const showLoginLink = isUsedLinkError(errorMessage);
  const disabled = Boolean(linkSetupError) || isLoading;
  const canSubmit = Boolean(month && day && year) && !disabled;

  const dayOptions = useMemo(() => {
    const count = daysInMonth(month, year);
    return Array.from({ length: count }, (_, i) => String(i + 1).padStart(2, "0"));
  }, [month, year]);

  const onMonthChange = (value) => {
    setMonth(value);
    const maxDay = daysInMonth(value, year);
    if (day && Number(day) > maxDay) {
      setDay("");
    }
  };

  const onYearChange = (value) => {
    setYear(value);
    const maxDay = daysInMonth(month, value);
    if (day && Number(day) > maxDay) {
      setDay("");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (linkSetupError || !token) {
      return;
    }

    if (!month || !day || !year) {
      setSubmitError("Please enter a valid date of birth");
      return;
    }

    try {
      await validateToken({
        token,
        dateOfBirth: formatDobForApi(month, day, year),
      });
      onVerified();
    } catch (error) {
      const message =
        typeof error === "string"
          ? error
          : error?.message || "Date of birth does not match. Please try again.";
      setSubmitError(message);
    }
  };

  const selectClass = errorMessage ? classes.errorSelect : classes.select;

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <div className={classes.logo}>
          <img src={samaritanLogo} alt="samaritan" height={56} />
        </div>
        <h1 className={classes.title}>Confirm Your Date of Birth</h1>
        <p className={classes.subtitle}>
          For confirmation, please enter your date of birth to access your profile.
        </p>
        <form className={classes.form} onSubmit={onSubmit}>
          {errorMessage ? (
            <div className={classes.error}>
              <div>{errorMessage}</div>
              {showLoginLink ? (
                <button
                  type="button"
                  className={classes.loginLink}
                  onClick={onGoToLogin}
                >
                  Go To Login Page
                </button>
              ) : null}
            </div>
          ) : null}
          <div className={classes.dobRow}>
            <label className={classes.selectGroup} htmlFor="magic-link-month">
              <span className={classes.selectLabel}>Month</span>
              <select
                id="magic-link-month"
                className={selectClass}
                value={month}
                disabled={disabled}
                onChange={(e) => onMonthChange(e.target.value)}
              >
                <option value="">Month</option>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <label className={classes.selectGroup} htmlFor="magic-link-day">
              <span className={classes.selectLabel}>Day</span>
              <select
                id="magic-link-day"
                className={selectClass}
                value={day}
                disabled={disabled}
                onChange={(e) => setDay(e.target.value)}
              >
                <option value="">Day</option>
                {dayOptions.map((d) => (
                  <option key={d} value={d}>
                    {Number(d)}
                  </option>
                ))}
              </select>
            </label>
            <label className={classes.selectGroup} htmlFor="magic-link-year">
              <span className={classes.selectLabel}>Year</span>
              <select
                id="magic-link-year"
                className={selectClass}
                value={year}
                disabled={disabled}
                onChange={(e) => onYearChange(e.target.value)}
              >
                <option value="">Year</option>
                {YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className={classes.submitWrap}>
            {isLoading ? (
              <LoadingCircle />
            ) : (
              <ButtonCTA1
                className={classes.continueButton}
                style={{
                  color: "#fbfbff",
                  backgroundColor: "#7378e8",
                  padding: "12px 26px",
                  opacity: canSubmit ? 1 : 0.5,
                  border: "none",
                  boxShadow: "none",
                  borderRadius: "10px",
                }}
                type="submit"
                invert="true"
                disabled={!canSubmit}
              >
                Continue
              </ButtonCTA1>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

MagicLinkDobGate.propTypes = {
  token: PropTypes.string.isRequired,
  linkSetupError: PropTypes.string,
  onVerified: PropTypes.func.isRequired,
  onGoToLogin: PropTypes.func.isRequired,
};

MagicLinkDobGate.defaultProps = {
  linkSetupError: null,
};

export default MagicLinkDobGate;
